import { z } from 'zod';

export interface ValidationResponse {
  success: boolean;
  error?: string;
}

const allowedCharsRegex =
  /[\p{L}\p{N}\p{P}\p{Z}\p{Sm}\p{Emoji}\p{Emoji_Presentation}\p{Emoji_Modifier}\p{Emoji_Component}\p{Emoji_Modifier_Base}]/u;

const getInvalidCharMessage = (char: string) => `validations.common.invalidCharacter||${char}`;

const validateChars = (text: string, fieldName: string) => {
  for (const char of text) {
    if (!allowedCharsRegex.test(char)) {
      return new z.ZodError([
        {
          code: 'custom',
          path: [fieldName],
          message: getInvalidCharMessage(char),
        },
      ]);
    }
  }
  return true;
};

const chatMessageSchema = z
  .string()
  .min(1, 'validations.chatMessages.empty')
  .max(500, 'validations.chatMessages.maxLength')
  .transform((val) => val.replace(/\r\n/g, '\n'))
  .superRefine((arg, ctx) => {
    const result = validateChars(arg, 'message');
    if (result instanceof z.ZodError) {
      ctx.addIssue({
        code: 'custom',
        message: result.issues[0]?.message || 'validations.common.commonTextError',
      });
    }
  });

const sessionFeedbackSchema = z.object({
  feedbackText: z
    .string()
    .max(500, 'validations.sessionFeedbacks.maxChars')
    .transform((val) => val?.replace(/\r\n/g, '\n'))
    .refine((text) => !text || text.trim().length > 0, {
      message: 'validations.sessionFeedbacks.onlyWhitespace',
    })
    .superRefine((arg, ctx) => {
      const result = validateChars(arg, 'free text');
      if (result instanceof z.ZodError) {
        ctx.addIssue({
          code: 'custom',
          message: result.issues[0]?.message || 'validations.common.commonTextError',
        });
      }
    })
    .optional()
    .transform((val) => val || ''),
});

const messageFeedbackSchema = z.object({
  freeText: z
    .string()
    .max(500, 'validations.messageFeedbackSchema.maxChars')
    .transform((val) => val?.replace(/\r\n/g, '\n'))
    .superRefine((arg, ctx) => {
      const result = validateChars(arg, 'free text');
      if (result instanceof z.ZodError) {
        ctx.addIssue({
          code: 'custom',
          message: result.issues[0]?.message || 'validations.messageFeedbackSchema.commonTextError',
        });
      }
    })
    .optional()
    .transform((val) => val || ''),
});

export const validateChatMessage = (message: string, isSending = false): ValidationResponse => {
  try {
    if (!isSending && !message) {
      return { success: true };
    }
    chatMessageSchema.parse(message);
    return { success: true };
  } catch (error) {
    if (error instanceof z.ZodError) {
      return {
        success: false,
        error: error.issues[0]?.message || 'validations.common.invalidMessage',
      };
    }
    return {
      success: false,
      error: 'validations.common.invalidFeedback',
    };
  }
};

export const validateSessionFeedback = (feedback: {
  rating?: number | null;
  feedbackText?: string;
}): ValidationResponse => {
  try {
    sessionFeedbackSchema.parse(feedback);
    return { success: true };
  } catch (error) {
    if (error instanceof z.ZodError) {
      return {
        success: false,
        error: error.issues[0]?.message || 'validations.common.invalidFeedback',
      };
    }
    return {
      success: false,
      error: 'validations.common.invalidFeedback',
    };
  }
};

export const validateMessageFeedback = (feedback: {
  freeText?: string;
  thumbs?: 'up' | 'down';
}): ValidationResponse => {
  try {
    messageFeedbackSchema.parse(feedback);
    return { success: true };
  } catch (error) {
    if (error instanceof z.ZodError) {
      return {
        success: false,
        error: error.issues[0]?.message || 'validations.common.invalidFeedback',
      };
    }
    return {
      success: false,
      error: 'validations.common.invalidFeedback',
    };
  }
};
